/**
 * The outreach mailboxes plus the two writes the sender bar offers: the
 * global kill switch and the first registration. The B2G campaign page
 * carries the sender bar – only its waves send through these mailboxes.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { B2G_KEY, b2gApi } from "../../api/b2g";
import { useToast } from "../../components/toast/context";

const POLL_MS = 30_000;

export function useSenderControls() {
  const qc = useQueryClient();
  const toast = useToast();
  const invalidate = () => qc.invalidateQueries({ queryKey: B2G_KEY });

  const senders = useQuery({ queryKey: [...B2G_KEY, "senders"], queryFn: b2gApi.senders, refetchInterval: POLL_MS });

  const kill = useMutation({
    mutationFn: ({ paused, reason }: { paused: boolean; reason?: string }) => b2gApi.killSwitch(paused, reason),
    onSuccess: (_d, { paused }) => {
      toast.ok(paused ? "All outreach sending stopped." : "Outreach sending resumed.");
      return invalidate();
    },
    onError: (err) => toast.error(err),
  });

  const register = useMutation({
    mutationFn: (address: string) => b2gApi.registerSender(address),
    onSuccess: () => {
      toast.ok("Outreach mailbox registered – warm-up starts today.");
      return invalidate();
    },
    onError: (err) => toast.error(err),
  });

  return {
    query: senders,
    killing: kill.isPending,
    registering: register.isPending,
    onKill: (paused: boolean, reason?: string) => kill.mutate({ paused, reason }),
    onRegister: (address: string) => register.mutate(address),
  };
}
